import { useEffect, useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProductDetailPanel } from './ProductDetailPanel';

// codex-reviewer on #5536 (minor, rounds 3 and 5): the API already supports ?status= filtering
// (routes/feed-imports.js's products-list route) and the design calls for it, but this
// component only ever exposed name search, then only a subset of statuses once the filter was
// added. syncFinalRunProductStatuses can copy ANY value aeo_page_generations.status legally
// holds — enumerated here as the full aeo_page_generations_status_check set (draft, published,
// archived, validated, needs_research, needs_review, pending_review, rejected,
// regen_in_progress, approved), plus recordRunProductOutcome's own null-pageGenerationId values
// ('failed' for context-prep/storage failures round 3/4; 'skipped' for AEO-ineligible/
// quality-gate/bulk-filter permanent exclusions round 4 — neither ever reaches a page at all).
const STATUS_OPTIONS = [
  'draft', 'needs_review', 'needs_research', 'pending_review', 'rejected', 'regen_in_progress',
  'validated', 'approved', 'published', 'archived', 'failed', 'skipped',
  // #6024 (codex-reviewer-2, minor): the two prep-time deferral outcomes the ledger writes (task-9l7l36x12d) — the
  // funnel counts them, so the drill-down must be able to isolate them too.
  'held', 'enrichment_deferred',
];
const ALL_STATUSES = '_all';

interface ProductRow {
  // codex-reviewer + codex-reviewer-2 on #5536, round 3: a product that failed before any page
  // was ever generated has no page_generation_id — the row still appears (status: 'failed'),
  // but with a null id, since there is no page to drill into detail for.
  pageGenerationId: string | null;
  sourceId: string;
  productName: string | null;
  status: string;
  hasRuleTelemetry: boolean;
}

export function ProductDrillDown({ runId }: { runId: string }) {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(ALL_STATUSES);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 25;

  useEffect(() => {
    // codex-reviewer-2 on #5536: without this guard, a slower earlier response (e.g. from a
    // stale search term or page) could resolve after a newer one and overwrite it — a classic
    // request race. Same `cancelled` pattern already used by ProductDetailPanel/GenerationReport.
    let cancelled = false;
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) params.set('search', search);
    if (status !== ALL_STATUSES) params.set('status', status);
    setError(null);
    authFetch(`/feed-imports/runs/${runId}/report/products?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Request failed');
        }
        return res.json();
      })
      .then((json: { products?: ProductRow[]; total?: number }) => {
        if (cancelled) return;
        setProducts(json.products ?? []);
        setTotal(json.total ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load products');
        setProducts([]);
        setTotal(0);
      });
    return () => { cancelled = true; };
  }, [runId, page, search, status]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder="Search products by name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {error ? (
        <p className="text-destructive text-sm">Could not load products: {error}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rule impact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p, i) => (
              <TableRow
                key={p.pageGenerationId ?? `no-page-${p.sourceId}-${i}`}
                className={p.pageGenerationId ? 'cursor-pointer' : undefined}
                onClick={() => { if (p.pageGenerationId) setSelectedId(p.pageGenerationId); }}
              >
                <TableCell>{p.productName ?? <span className="text-muted-foreground italic">(no name recorded)</span>}</TableCell>
                <TableCell><Badge variant="outline">{p.status}</Badge></TableCell>
                <TableCell>{p.hasRuleTelemetry ? 'View' : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{total.toLocaleString()} products</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
      {selectedId && (
        <ProductDetailPanel runId={runId} pageGenerationId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
